import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Mail, Check } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const r = await axios.post(
        `${BACKEND}/api/auth/forgot-password`,
        { email: email.trim() },
        { withCredentials: true },
      );
      setDone({ ok: true, reset_url: r.data.reset_url });
    } catch (e) {
      const d = e.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Something went wrong. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-10 relative">
      <div className="w-full max-w-md relative z-[2]">
        <div className="text-center mb-8">
          <div className="kpi-label">Reset password</div>
          <h1 className="display text-4xl mt-2 font-bold uppercase tracking-tight leading-none">
            Command<span className="text-oud">·</span>Center
          </h1>
          <div className="hairline-oud w-24 mx-auto mt-4" />
          <div className="serif-mark text-sm text-muted-foreground mt-3 tracking-wide">
            For CEO's Only
          </div>
        </div>

        {done ? (
          <div className="hud-frame p-8 space-y-5" data-testid="forgot-success">
            <div className="tread-plate h-1.5 -mx-8 -mt-8 mb-4" />
            <div className="flex items-center gap-3">
              <span className="hud-chip hud-chip-on"><Check size={11} /> Sent</span>
            </div>
            <p className="text-sm leading-relaxed">
              If <span className="mono">{email}</span> matches an account, we've prepared a reset link.
            </p>
            {done.reset_url ? (
              <div className="space-y-3">
                <div className="kpi-label" style={{ color: "hsl(var(--signal-amber))" }}>
                  Email isn't wired yet — use this link directly:
                </div>
                <Link
                  to={done.reset_url.replace(window.location.origin, "")}
                  className="hud-btn hud-btn-primary w-full justify-center"
                  data-testid="forgot-direct-link"
                >
                  Reset my password now <ArrowRight size={11} />
                </Link>
                <div className="mono text-[9px] text-muted-foreground uppercase tracking-widest text-center">
                  Link valid for 1 hour.
                </div>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Check your inbox in a minute or two. The link is valid for 1 hour.
              </p>
            )}
            <div className="text-center mono text-[11px] uppercase tracking-widest text-muted-foreground">
              <Link to="/signin" className="text-foreground hover:text-hud">
                Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="hud-frame p-8 space-y-5" data-testid="forgot-form">
            <div className="tread-plate h-1.5 -mx-8 -mt-8 mb-4" />
            <p className="text-sm text-muted-foreground">
              Enter your account email and we'll send you a link to set a new password.
            </p>
            <div>
              <div className="kpi-label">Email</div>
              <input
                type="email"
                autoFocus
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="hud-input mt-2"
                placeholder="you@example.com"
                required
                data-testid="forgot-email"
              />
            </div>
            {err && (
              <div className="text-[hsl(var(--oxblood))] mono text-xs uppercase tracking-widest">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="hud-btn hud-btn-primary w-full justify-center"
              data-testid="forgot-submit"
            >
              <Mail size={11} /> {busy ? "Sending…" : "Send reset link"}
            </button>
          </form>
        )}

        {!done && (
          <div className="mt-5 text-center mono text-[11px] uppercase tracking-widest text-muted-foreground">
            <Link to="/signin" className="text-foreground hover:text-hud" data-testid="forgot-to-signin">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
